import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render errors so one broken component does not blank the whole app.
 *
 * Still a class component: React has no hook equivalent of
 * `componentDidCatch`.
 */
export class ErrorBoundary extends Component<Props, State> {
  public override state: State = { error: null };

  public static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  public override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Left as console output deliberately. Wiring this to a reporting service is
    // a decision with privacy implications — an internal CRM's stack traces can
    // carry customer names — so it is not switched on by default.
    console.error('Unhandled render error:', error, info.componentStack);
  }

  public override render(): ReactNode {
    const { error } = this.state;

    if (!error) {
      return this.props.children;
    }

    return (
      <Container maxWidth="sm">
        <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center' }}>
          <Stack spacing={2} sx={{ alignItems: 'flex-start' }}>
            <Typography variant="h2">Something went wrong</Typography>

            <Typography color="text.secondary">
              This page failed to load. Reloading usually fixes it. If it keeps
              happening, let the team know what you were doing at the time.
            </Typography>

            <Typography
              variant="body2"
              component="pre"
              sx={{
                p: 2,
                width: '100%',
                overflowX: 'auto',
                bgcolor: 'surfaceRaised',
                borderRadius: 2,
                fontFamily: 'ui-monospace, monospace',
              }}
            >
              {error.message}
            </Typography>

            <Button variant="contained" onClick={() => window.location.reload()}>
              Reload the page
            </Button>
          </Stack>
        </Box>
      </Container>
    );
  }
}
